import React from 'react';

export const TypographyDemo = ({ colors }) => {
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGB for transparency effects
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Design System Typography</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Complete typography hierarchy and code syntax highlighting with your color palette.
                </p>
            </div>

            {/* Typography Scale & Hierarchy */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Typography Scale</h5>

                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Display Text */}
                        <div className="space-y-4">
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">DISPLAY / 48px</span>
                            </div>
                            <h1 className="text-5xl font-bold leading-tight" style={{ color: getColor(0) }}>
                                Design System
                            </h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Used for hero headlines and major page titles
                            </p>
                        </div>

                        {/* Headings Hierarchy */}
                        <div className="space-y-3">
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">HEADINGS</span>
                            </div>
                            <h1 className="text-4xl font-bold" style={{ color: getColor(0) }}>H1 Heading / 36px</h1>
                            <h2 className="text-3xl font-semibold" style={{ color: getColor(1) }}>H2 Heading / 30px</h2>
                            <h3 className="text-2xl font-semibold" style={{ color: getColor(2) }}>H3 Heading / 24px</h3>
                            <h4 className="text-xl font-medium" style={{ color: getColor(3) }}>H4 Heading / 20px</h4>
                            <h5 className="text-lg font-medium text-gray-900 dark:text-white">H5 Heading / 18px</h5>
                            <h6 className="text-base font-medium text-gray-700 dark:text-gray-300">H6 Heading / 16px</h6>
                        </div>
                    </div>

                    {/* Body Text Variants */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                        <div>
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-3">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">BODY LARGE / 18px</span>
                            </div>
                            <p className="text-lg text-gray-800 dark:text-gray-200 leading-relaxed">
                                This is large body text used for introductory paragraphs and important content that needs emphasis.
                            </p>
                        </div>

                        <div>
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-3">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">BODY / 16px</span>
                            </div>
                            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                Standard body text for most content. This is the default size for articles, descriptions, and general reading.
                            </p>
                        </div>

                        <div>
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-3">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">SMALL / 14px</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                                Smaller text for captions, metadata, and secondary information that supports the main content.
                            </p>
                        </div>
                    </div>

                    {/* Interactive Text Elements */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                        <div>
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">INTERACTIVE ELEMENTS</span>
                            </div>
                            <div className="space-y-3">
                                <a href="#" className="block text-base font-medium hover:opacity-80 transition-opacity" style={{ color: getColor(0) }}>
                                    Primary Link
                                </a>
                                <a href="#" className="block text-base underline hover:no-underline transition-all" style={{ color: getColor(1) }}>
                                    Underlined Link
                                </a>
                                <button className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: getColor(2) }}>
                                    Button Text
                                </button>
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white" style={{ backgroundColor: getColor(3) }}>
                                    Badge Text
                                </span>
                            </div>
                        </div>

                        <div>
                            <div className="border-b border-gray-200 dark:border-gray-600 pb-2 mb-4">
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">TEXT EMPHASIS</span>
                            </div>
                            <div className="space-y-2">
                                <p className="text-base text-gray-700 dark:text-gray-300">
                                    This is regular text with <strong style={{ color: getColor(0) }}>strong emphasis</strong> and
                                    <em style={{ color: getColor(1) }}> italic emphasis</em>.
                                </p>
                                <p className="text-base text-gray-700 dark:text-gray-300">
                                    You can also use <mark style={{ backgroundColor: hexToRgba(getColor(2), 0.2), color: getColor(2) }}>highlighted text</mark> for emphasis.
                                </p>
                                <p className="text-base">
                                    <span style={{ color: getColor(3) }}>Colored text</span> can be used sparingly for accent.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Code Syntax Highlighting Theme */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Code Syntax Theme</h5>

                {/* IDE Theme Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Dark Theme */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Dark Theme</span>
                            <div className="flex gap-1">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                        </div>
                        <div
                            className="rounded-lg p-4 text-sm font-mono leading-relaxed overflow-x-auto custom-scrollbar"
                            style={{ backgroundColor: '#1a1a1a' }}
                        >
                            <div className="space-y-1">
                                <div>
                                    <span style={{ color: getColor(2) }}>class</span>{' '}
                                    <span style={{ color: getColor(0) }}>DataProcessor</span>
                                    <span style={{ color: '#ffffff' }}>:</span>
                                </div>
                                <div className="ml-4">
                                    <span style={{ color: getColor(2) }}>def</span>{' '}
                                    <span style={{ color: getColor(1) }}>__init__</span>
                                    <span style={{ color: '#ffffff' }}>(</span>
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>, </span>
                                    <span style={{ color: getColor(4) || '#999' }}>config</span>
                                    <span style={{ color: '#ffffff' }}>):</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>.config = </span>
                                    <span style={{ color: getColor(4) || '#999' }}>config</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>.data = </span>
                                    <span style={{ color: '#ffffff' }}>[]</span>
                                </div>
                                <div className="h-2"></div>
                                <div className="ml-4">
                                    <span style={{ color: getColor(2) }}>def</span>{' '}
                                    <span style={{ color: getColor(1) }}>process</span>
                                    <span style={{ color: '#ffffff' }}>(</span>
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>, </span>
                                    <span style={{ color: getColor(4) || '#999' }}>input_data</span>
                                    <span style={{ color: '#ffffff' }}>):</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: '#888' }}># Process the input data</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(2) }}>for</span>{' '}
                                    <span style={{ color: getColor(4) || '#999' }}>item</span>
                                    <span style={{ color: '#ffffff' }}> in </span>
                                    <span style={{ color: getColor(4) || '#999' }}>input_data</span>
                                    <span style={{ color: '#ffffff' }}>:</span>
                                </div>
                                <div className="ml-12">
                                    <span style={{ color: getColor(2) }}>if</span>{' '}
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>.</span>
                                    <span style={{ color: getColor(1) }}>validate</span>
                                    <span style={{ color: '#ffffff' }}>(</span>
                                    <span style={{ color: getColor(4) || '#999' }}>item</span>
                                    <span style={{ color: '#ffffff' }}>):</span>
                                </div>
                                <div className="ml-16">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#ffffff' }}>.data.</span>
                                    <span style={{ color: getColor(1) }}>append</span>
                                    <span style={{ color: '#ffffff' }}>(</span>
                                    <span style={{ color: getColor(4) || '#999' }}>item</span>
                                    <span style={{ color: '#ffffff' }}>)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Light Theme */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Light Theme</span>
                            <div className="flex gap-1">
                                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                            </div>
                        </div>
                        <div
                            className="rounded-lg p-4 text-sm font-mono leading-relaxed overflow-x-auto custom-scrollbar border"
                            style={{ backgroundColor: '#fafafa', borderColor: hexToRgba(getColor(0), 0.2) }}
                        >
                            <div className="space-y-1">
                                <div>
                                    <span style={{ color: getColor(2), fontWeight: 'bold' }}>class</span>{' '}
                                    <span style={{ color: getColor(0), fontWeight: 'bold' }}>DataProcessor</span>
                                    <span style={{ color: '#333' }}>:</span>
                                </div>
                                <div className="ml-4">
                                    <span style={{ color: getColor(2), fontWeight: 'bold' }}>def</span>{' '}
                                    <span style={{ color: getColor(1) }}>__init__</span>
                                    <span style={{ color: '#333' }}>(</span>
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>, </span>
                                    <span style={{ color: '#666' }}>config</span>
                                    <span style={{ color: '#333' }}>):</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>.config = </span>
                                    <span style={{ color: '#666' }}>config</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>.data = </span>
                                    <span style={{ color: '#333' }}>[]</span>
                                </div>
                                <div className="h-2"></div>
                                <div className="ml-4">
                                    <span style={{ color: getColor(2), fontWeight: 'bold' }}>def</span>{' '}
                                    <span style={{ color: getColor(1) }}>process</span>
                                    <span style={{ color: '#333' }}>(</span>
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>, </span>
                                    <span style={{ color: '#666' }}>input_data</span>
                                    <span style={{ color: '#333' }}>):</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: '#999' }}># Process the input data</span>
                                </div>
                                <div className="ml-8">
                                    <span style={{ color: getColor(2), fontWeight: 'bold' }}>for</span>{' '}
                                    <span style={{ color: '#666' }}>item</span>
                                    <span style={{ color: '#333' }}> in </span>
                                    <span style={{ color: '#666' }}>input_data</span>
                                    <span style={{ color: '#333' }}>:</span>
                                </div>
                                <div className="ml-12">
                                    <span style={{ color: getColor(2), fontWeight: 'bold' }}>if</span>{' '}
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>.</span>
                                    <span style={{ color: getColor(1) }}>validate</span>
                                    <span style={{ color: '#333' }}>(</span>
                                    <span style={{ color: '#666' }}>item</span>
                                    <span style={{ color: '#333' }}>):</span>
                                </div>
                                <div className="ml-16">
                                    <span style={{ color: getColor(3) }}>self</span>
                                    <span style={{ color: '#333' }}>.data.</span>
                                    <span style={{ color: getColor(1) }}>append</span>
                                    <span style={{ color: '#333' }}>(</span>
                                    <span style={{ color: '#666' }}>item</span>
                                    <span style={{ color: '#333' }}>)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Terminal Theme */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Terminal Theme</h5>

                <div
                    className="rounded-lg p-4 text-sm font-mono leading-relaxed"
                    style={{ backgroundColor: '#0d1117', color: '#c9d1d9' }}
                >
                    {/* Terminal Header */}
                    <div className="flex items-center justify-between mb-4 pb-2 border-b" style={{ borderColor: hexToRgba(getColor(0), 0.3) }}>
                        <div className="flex gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        </div>
                        <span className="text-xs" style={{ color: getColor(1) }}>zsh - obelisk@portfolio</span>
                    </div>

                    <div className="space-y-2">
                        <div className="flex">
                            <span style={{ color: getColor(0) }}>➜</span>
                            <span style={{ color: getColor(1) }}> workspace</span>
                            <span style={{ color: getColor(2) }}> git:(</span>
                            <span style={{ color: getColor(3) }}>main</span>
                            <span style={{ color: getColor(2) }}>) </span>
                            <span style={{ color: '#c9d1d9' }}>python data_analysis.py</span>
                        </div>

                        <div style={{ color: getColor(1) }}>
                            Starting data analysis pipeline...
                        </div>

                        <div style={{ color: getColor(2) }}>
                            [INFO] Loading dataset: customers.csv
                        </div>

                        <div style={{ color: getColor(3) }}>
                            [SUCCESS] Processed 10,247 records
                        </div>

                        <div style={{ color: getColor(0) }}>
                            [WARNING] Missing values detected in 3 columns
                        </div>

                        <div className="pt-2">
                            <div className="flex">
                                <span style={{ color: getColor(0) }}>➜</span>
                                <span style={{ color: getColor(1) }}> workspace</span>
                                <span style={{ color: getColor(2) }}> git:(</span>
                                <span style={{ color: getColor(3) }}>main</span>
                                <span style={{ color: getColor(2) }}>) </span>
                                <span style={{ color: '#c9d1d9' }}>ls -la</span>
                            </div>
                        </div>

                        <div className="ml-4 space-y-1 text-xs">
                            <div>
                                <span style={{ color: getColor(2) }}>drwxr-xr-x</span>
                                <span style={{ color: '#c9d1d9' }}> 5 user staff  160 Dec 15 10:30 </span>
                                <span style={{ color: getColor(0) }}>data/</span>
                            </div>
                            <div>
                                <span style={{ color: getColor(1) }}>-rw-r--r--</span>
                                <span style={{ color: '#c9d1d9' }}> 1 user staff 2.1K Dec 15 10:28 </span>
                                <span style={{ color: getColor(3) }}>analysis.py</span>
                            </div>
                            <div>
                                <span style={{ color: getColor(1) }}>-rw-r--r--</span>
                                <span style={{ color: '#c9d1d9' }}> 1 user staff  847 Dec 15 09:15 </span>
                                <span style={{ color: '#c9d1d9' }}>requirements.txt</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Extended Code Example */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-4 text-gray-900 dark:text-white">Extended Code Example</h5>

                <div
                    className="rounded-lg p-6 text-sm font-mono leading-relaxed overflow-x-auto custom-scrollbar"
                    style={{ backgroundColor: '#1e1e1e' }}
                >
                    <div className="space-y-1">
                        {/* Import statements */}
                        <div>
                            <span style={{ color: getColor(2) }}>import</span>{' '}
                            <span style={{ color: getColor(1) }}>pandas</span>{' '}
                            <span style={{ color: getColor(2) }}>as</span>{' '}
                            <span style={{ color: getColor(1) }}>pd</span>
                        </div>
                        <div>
                            <span style={{ color: getColor(2) }}>import</span>{' '}
                            <span style={{ color: getColor(1) }}>numpy</span>{' '}
                            <span style={{ color: getColor(2) }}>as</span>{' '}
                            <span style={{ color: getColor(1) }}>np</span>
                        </div>
                        <div>
                            <span style={{ color: getColor(2) }}>from</span>{' '}
                            <span style={{ color: getColor(1) }}>sklearn.model_selection</span>{' '}
                            <span style={{ color: getColor(2) }}>import</span>{' '}
                            <span style={{ color: getColor(1) }}>train_test_split</span>
                        </div>
                        <div>
                            <span style={{ color: getColor(2) }}>from</span>{' '}
                            <span style={{ color: getColor(1) }}>sklearn.ensemble</span>{' '}
                            <span style={{ color: getColor(2) }}>import</span>{' '}
                            <span style={{ color: getColor(1) }}>RandomForestClassifier</span>
                        </div>
                        <div>
                            <span style={{ color: getColor(2) }}>from</span>{' '}
                            <span style={{ color: getColor(1) }}>sklearn.metrics</span>{' '}
                            <span style={{ color: getColor(2) }}>import</span>{' '}
                            <span style={{ color: getColor(1) }}>accuracy_score</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(1) }}>classification_report</span>
                        </div>
                        <div className="h-3"></div>

                        {/* Class definition */}
                        <div>
                            <span style={{ color: getColor(2) }}>class</span>{' '}
                            <span style={{ color: getColor(0) }}>MLPipeline</span>
                            <span style={{ color: '#ffffff' }}>:</span>
                        </div>
                        <div className="ml-4">
                            <span style={{ color: '#888' }}>"""</span>
                        </div>
                        <div className="ml-4">
                            <span style={{ color: '#888' }}>A comprehensive machine learning pipeline for data processing</span>
                        </div>
                        <div className="ml-4">
                            <span style={{ color: '#888' }}>and model training with built-in validation and reporting.</span>
                        </div>
                        <div className="ml-4">
                            <span style={{ color: '#888' }}>"""</span>
                        </div>
                        <div className="h-2"></div>

                        <div className="ml-4">
                            <span style={{ color: getColor(2) }}>def</span>{' '}
                            <span style={{ color: getColor(1) }}>__init__</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(4) || '#999' }}>config</span>
                            <span style={{ color: '#ffffff' }}>=</span>
                            <span style={{ color: getColor(2) }}>None</span>
                            <span style={{ color: '#ffffff' }}>):</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.config = </span>
                            <span style={{ color: getColor(4) || '#999' }}>config</span>{' '}
                            <span style={{ color: getColor(2) }}>or</span>{' '}
                            <span style={{ color: '#ffffff' }}>&#123;&#125;</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data = </span>
                            <span style={{ color: getColor(2) }}>None</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.model = </span>
                            <span style={{ color: getColor(2) }}>None</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.is_trained = </span>
                            <span style={{ color: getColor(2) }}>False</span>
                        </div>
                        <div className="h-3"></div>

                        {/* Method definitions */}
                        <div className="ml-4">
                            <span style={{ color: getColor(2) }}>def</span>{' '}
                            <span style={{ color: getColor(1) }}>load_data</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(4) || '#999' }}>filepath</span>
                            <span style={{ color: '#ffffff' }}>: </span>
                            <span style={{ color: getColor(0) }}>str</span>
                            <span style={{ color: '#ffffff' }}>):</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: '#888' }}># Load and validate data from CSV file</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(2) }}>try</span>
                            <span style={{ color: '#ffffff' }}>:</span>
                        </div>
                        <div className="ml-12">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data = </span>
                            <span style={{ color: getColor(1) }}>pd</span>
                            <span style={{ color: '#ffffff' }}>.</span>
                            <span style={{ color: getColor(1) }}>read_csv</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(4) || '#999' }}>filepath</span>
                            <span style={{ color: '#ffffff' }}>)</span>
                        </div>
                        <div className="ml-12">
                            <span style={{ color: getColor(1) }}>print</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(0) }}>f"Loaded &#123;</span>
                            <span style={{ color: getColor(1) }}>len</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data)</span>
                            <span style={{ color: getColor(0) }}>&#125; records from &#123;</span>
                            <span style={{ color: getColor(4) || '#999' }}>filepath</span>
                            <span style={{ color: getColor(0) }}>&#125;"</span>
                            <span style={{ color: '#ffffff' }}>)</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(2) }}>except</span>{' '}
                            <span style={{ color: getColor(0) }}>Exception</span>{' '}
                            <span style={{ color: getColor(2) }}>as</span>{' '}
                            <span style={{ color: getColor(4) || '#999' }}>e</span>
                            <span style={{ color: '#ffffff' }}>:</span>
                        </div>
                        <div className="ml-12">
                            <span style={{ color: getColor(1) }}>print</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(0) }}>f"Error loading data: &#123;</span>
                            <span style={{ color: getColor(4) || '#999' }}>e</span>
                            <span style={{ color: getColor(0) }}>&#125;"</span>
                            <span style={{ color: '#ffffff' }}>)</span>
                        </div>
                        <div className="ml-12">
                            <span style={{ color: getColor(2) }}>return</span>{' '}
                            <span style={{ color: getColor(2) }}>False</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(2) }}>return</span>{' '}
                            <span style={{ color: getColor(2) }}>True</span>
                        </div>
                        <div className="h-3"></div>

                        <div className="ml-4">
                            <span style={{ color: getColor(2) }}>def</span>{' '}
                            <span style={{ color: getColor(1) }}>preprocess</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(4) || '#999' }}>target_column</span>
                            <span style={{ color: '#ffffff' }}>: </span>
                            <span style={{ color: getColor(0) }}>str</span>
                            <span style={{ color: '#ffffff' }}>):</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: '#888' }}># Clean and prepare data for training</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(2) }}>if</span>{' '}
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data </span>
                            <span style={{ color: getColor(2) }}>is</span>{' '}
                            <span style={{ color: getColor(2) }}>None</span>
                            <span style={{ color: '#ffffff' }}>:</span>
                        </div>
                        <div className="ml-12">
                            <span style={{ color: getColor(2) }}>raise</span>{' '}
                            <span style={{ color: getColor(0) }}>ValueError</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(0) }}>"No data loaded. Call load_data() first."</span>
                            <span style={{ color: '#ffffff' }}>)</span>
                        </div>
                        <div className="h-2"></div>
                        <div className="ml-8">
                            <span style={{ color: '#888' }}># Remove rows with missing target values</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data = </span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data.</span>
                            <span style={{ color: getColor(1) }}>dropna</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(4) || '#999' }}>subset</span>
                            <span style={{ color: '#ffffff' }}>=</span>
                            <span style={{ color: '#ffffff' }}>[</span>
                            <span style={{ color: getColor(4) || '#999' }}>target_column</span>
                            <span style={{ color: '#ffffff' }}>])</span>
                        </div>
                        <div className="h-2"></div>
                        <div className="ml-8">
                            <span style={{ color: '#888' }}># Separate features and target</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(4) || '#999' }}>X</span>{' '}
                            <span style={{ color: '#ffffff' }}> = </span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data.</span>
                            <span style={{ color: getColor(1) }}>drop</span>
                            <span style={{ color: '#ffffff' }}>(</span>
                            <span style={{ color: getColor(4) || '#999' }}>target_column</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(4) || '#999' }}>axis</span>
                            <span style={{ color: '#ffffff' }}>=</span>
                            <span style={{ color: getColor(3) }}>1</span>
                            <span style={{ color: '#ffffff' }}>)</span>
                        </div>
                        <div className="ml-8">
                            <span style={{ color: getColor(4) || '#999' }}>y</span>{' '}
                            <span style={{ color: '#ffffff' }}> = </span>
                            <span style={{ color: getColor(3) }}>self</span>
                            <span style={{ color: '#ffffff' }}>.data</span>
                            <span style={{ color: '#ffffff' }}>[</span>
                            <span style={{ color: getColor(4) || '#999' }}>target_column</span>
                            <span style={{ color: '#ffffff' }}>]</span>
                        </div>
                        <div className="h-2"></div>
                        <div className="ml-8">
                            <span style={{ color: getColor(2) }}>return</span>{' '}
                            <span style={{ color: getColor(4) || '#999' }}>X</span>
                            <span style={{ color: '#ffffff' }}>, </span>
                            <span style={{ color: getColor(4) || '#999' }}>y</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Article/Blog Example */}
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Article Layout</h5>

                <article className="max-w-4xl">
                    <header className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-600">
                        <h1 className="text-4xl font-bold mb-4 leading-tight" style={{ color: getColor(0) }}>
                            The Future of Design Systems: Building Scalable Visual Languages
                        </h1>
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <span>December 15, 2024</span>
                            <span>•</span>
                            <span>12 min read</span>
                            <span>•</span>
                            <span style={{ color: getColor(1) }}>Design Systems</span>
                        </div>
                        <p className="text-xl text-gray-700 dark:text-gray-300 mt-4 leading-relaxed">
                            How modern design systems are evolving to support complex digital products while maintaining consistency and accessibility across platforms.
                        </p>
                    </header>

                    <div className="prose max-w-none">
                        <h2 className="text-2xl font-semibold mt-8 mb-4" style={{ color: getColor(1) }}>
                            The Evolution of Design Systems
                        </h2>

                        <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                            Design systems have transformed from simple style guides into comprehensive ecosystems that encompass everything from color palettes and typography to complex component libraries and interaction patterns. This evolution reflects the growing complexity of digital products and the need for scalable design solutions.
                        </p>

                        <blockquote
                            className="border-l-4 pl-6 py-4 my-8 bg-gray-50 dark:bg-gray-900/50 rounded-r-lg"
                            style={{ borderLeftColor: getColor(2) }}
                        >
                            <p className="text-lg italic text-gray-800 dark:text-gray-200 mb-3">
                                "A design system is a living organism that breathes life into digital experiences, ensuring consistency while allowing for creative expression."
                            </p>
                            <cite className="text-sm font-medium" style={{ color: getColor(3) }}>
                                — Sarah Chen, Design Systems Lead at Figma
                            </cite>
                        </blockquote>

                        <h3 className="text-xl font-semibold mt-8 mb-4" style={{ color: getColor(2) }}>
                            Key Components of Modern Systems
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
                            <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">Design Tokens</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Atomic design decisions stored as data, enabling consistent styling across platforms and tools.
                                </p>
                            </div>
                            <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-600">
                                <h4 className="font-semibold mb-3" style={{ color: getColor(1) }}>Component Library</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Reusable UI elements with defined behaviors, states, and accessibility features.
                                </p>
                            </div>
                        </div>

                        <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                            The integration of <strong style={{ color: getColor(0) }}>design tokens</strong> has revolutionized how teams maintain consistency across multiple platforms. By abstracting design decisions into semantic variables, teams can ensure that a single source of truth governs everything from <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm" style={{ color: getColor(2) }}>button-primary-background</code> to <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm" style={{ color: getColor(2) }}>spacing-medium</code>.
                        </p>

                        <h3 className="text-xl font-semibold mt-8 mb-4" style={{ color: getColor(2) }}>
                            Implementation Strategies
                        </h3>

                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 my-6">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: getColor(0) }}>
                                    💡
                                </div>
                                <div>
                                    <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Pro Tip</h4>
                                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                                        Start small with a focused component library and expand based on actual usage patterns rather than theoretical needs.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                            As design systems mature, they become powerful tools for <em style={{ color: getColor(1) }}>scaling design decisions</em> across large organizations, enabling teams to move faster while maintaining quality and consistency.
                        </p>
                    </div>
                </article>
            </div>

            {/* Typography Specifications */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h5 className="text-base font-semibold mb-6 text-gray-900 dark:text-white">Typography Specifications</h5>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Font Properties</h6>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Font Family</span>
                                <span className="text-sm font-mono">Inter, system-ui, sans-serif</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Line Height</span>
                                <span className="text-sm font-mono">1.5 (body) / 1.2 (headings)</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                                <span className="text-sm text-gray-600 dark:text-gray-400">Letter Spacing</span>
                                <span className="text-sm font-mono">-0.01em (body) / -0.02em (headings)</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="text-sm font-semibold mb-4 text-gray-900 dark:text-white">Color Usage</h6>
                        <div className="space-y-3">
                            {colors.slice(0, 4).map((color, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div
                                        className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600"
                                        style={{ backgroundColor: color.value }}
                                    />
                                    <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">
                                        {['Primary headings', 'Secondary text', 'Accent elements', 'Supporting text'][index]}
                                    </span>
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                        {color.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};